import { useQuery } from '@tanstack/react-query'
import {
  Outlet,
  createFileRoute,
  linkOptions,
  useMatch,
} from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  type GroupFilters,
  type GroupResponse,
  type GroupSortingState,
  Role,
  groupSortingColumns,
} from 'types'
import z from 'zod'

import { queryGroupCount, queryGroupList } from '@api/group/group.queries'
import { querySession } from '@api/session/session.queries'

import StreamingExportDialog from '@/components/export/streaming-export-dialog'
import { ExportType } from '@/components/export/streaming-export-dialog.types'
import PortfolioSelect from '@/components/portfolio-select'
import SearchBar from '@/components/search-bar'
import type { CursorPaginationState } from '@/components/table/cursor-pagination'
import GroupsTable from '@/components/tables/groups/groups-table'
import { numberFilterSchema } from '@/lib/filter'
import { cursorPaginationSchema, sortingSchema } from '@/lib/table'
import { pageSearchAtom, storePageSearch } from '@/stores/page-search.atom'
import { portfolioFilterAtom } from '@/stores/portfolio-filter.atom'

import SectionHeader from '../../-components/section-layout/section-header'
import GroupCreateDialog from './-components/group-create-dialog'
import GroupsImportDialog from './-components/groups-import-dialog'
import GroupsStats from './-components/groups-stats'

const searchSchema = z.object({
  pagination: cursorPaginationSchema,
  sorting: sortingSchema(groupSortingColumns),
  filters: z
    .object({
      search: z.string().optional(),
      groupName: z.string().optional(),
      companiesCount: numberFilterSchema.optional(),
      policiesCount: numberFilterSchema.optional(),
      acceptanceRate: numberFilterSchema.optional(),
    })
    .optional(),
})

type Search = z.infer<typeof searchSchema>

export const Route = createFileRoute('/app/data/groups')({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context: { queryClient }, deps: { pagination, sorting } }) => {
    queryClient.prefetchQuery(
      queryGroupList({
        ...pagination,
        sorting,
      }),
    )
    queryClient.prefetchQuery(queryGroupCount())
  },
  beforeLoad: ({ search, cause, location }) => {
    storePageSearch(
      Route.fullPath,
      { to: Route.fullPath, search },
      location,
      cause,
    )
  },
})

function RouteComponent() {
  const { t } = useTranslation()
  const routeSearch = Route.useSearch()
  const navigate = Route.useNavigate()
  const portfolioFilter = useAtomValue(portfolioFilterAtom)
  const { data: session } = useQuery(querySession())

  const filters: GroupFilters = {
    ...routeSearch.filters,
    portfolioUserUuid: portfolioFilter,
  }

  // If user is on a group detail sheet,
  // don't change the search params to keep
  // the filters and sorting applied on the table.
  const pageSearch = useAtomValue(pageSearchAtom)
  const match = useMatch({
    shouldThrow: false,
    from: '/app/data/groups/$uuid',
  })
  const tableFrozen = !!match
  const search = tableFrozen
    ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      ((pageSearch[Route.fullPath]?.search as undefined | Search) ??
      routeSearch)
    : routeSearch

  const setFilters = (updater: (filter?: GroupFilters) => GroupFilters) =>
    navigate({
      to: '.',
      search: (previous) => ({
        ...previous,
        filters: updater(previous.filters),
      }),
    })

  const setPagination = (
    updater: (pagination: CursorPaginationState) => CursorPaginationState,
  ) =>
    navigate({
      to: '.',
      search: (previous) => ({
        ...previous,
        pagination: updater(previous.pagination),
      }),
    })

  const setSorting = (
    updater: (sorting?: GroupSortingState) => GroupSortingState,
  ) =>
    navigate({
      to: '.',
      search: (previous) => ({
        ...previous,
        sorting: updater(previous.sorting),
      }),
    })

  const handleSearchChange = (search: string) =>
    setFilters((previous) => ({ ...previous, search }))

  const rowLink = (row: GroupResponse) =>
    linkOptions({
      to: '/app/data/groups/$uuid',
      params: { uuid: row.uuid },
      viewTransition: false,
    })

  return (
    <div className="flex h-full grow flex-col divide-y overflow-hidden">
      <SectionHeader isSecondary title={t('data.groups.title')}>
        <div className="flex items-center gap-2">
          <SearchBar
            tabIndex={1}
            className="max-w-62.75"
            text={filters.search}
            setText={handleSearchChange}
          />
          <PortfolioSelect />
          <StreamingExportDialog type={ExportType.Groups} filters={filters} />
          <GroupCreateDialog />
          {session?.role === Role.Admin && <GroupsImportDialog />}
        </div>
      </SectionHeader>
      <div className="flex h-full flex-col gap-8 overflow-hidden p-2">
        <GroupsStats />
        <GroupsTable
          filters={filters}
          setFilters={setFilters}
          pagination={search.pagination}
          setPagination={setPagination}
          sorting={search.sorting}
          setSorting={setSorting}
          rowLink={rowLink}
        />
      </div>
      <Outlet />
    </div>
  )
}
